/**
 * Storage Adapter for SnapHub PWA.
 *
 * Provides a typed, unified interface for token storage:
 * - **Access Token** → SessionStorage (cleared when the tab closes)
 * - **Refresh Token** → LocalStorage (persists across sessions)
 *
 * No raw storage handles are exposed — consumers interact exclusively
 * through the typed get / set / clear methods.
 *
 * Validates: Requirements 45.1
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** SessionStorage key for the JWT access token. */
export const ACCESS_TOKEN_KEY = 'snaphub_access_token';

/** LocalStorage key for the JWT refresh token. */
export const REFRESH_TOKEN_KEY = 'snaphub_refresh_token';

// ---------------------------------------------------------------------------
// StorageAdapter class
// ---------------------------------------------------------------------------

export class StorageAdapter {
  // -- Access Token (SessionStorage) ----------------------------------------

  /** Read the current access token. Returns `null` when nothing is stored. */
  getAccessToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /** Store an access token in SessionStorage. */
  setAccessToken(token: string): void {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  /** Remove the access token from SessionStorage. */
  clearAccessToken(): void {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  // -- Refresh Token (LocalStorage) -----------------------------------------

  /** Read the current refresh token. Returns `null` when nothing is stored. */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /** Store a refresh token in LocalStorage. */
  setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  /** Remove the refresh token from LocalStorage. */
  clearRefreshToken(): void {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  // -- Convenience ----------------------------------------------------------

  /** Clear both access and refresh tokens. */
  clearAll(): void {
    this.clearAccessToken();
    this.clearRefreshToken();
  }
}

// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

/** Global storage adapter shared across the entire application. */
export const storageAdapter = new StorageAdapter();
